import React from 'react'
import { useTranslation } from '../hooks/use-i18n'

export interface DriverOption {
  label: string
  value: string
  car: string
}

interface Formula1SelectPositionModalProps {
  isOpen: boolean
  position: number
  availableDrivers: DriverOption[]
  onClose: () => void
  onSelect: (driver: string) => void
}

const positionSuffix = (position: number) => {
  if (position === 1) return 'st'
  if (position === 2) return 'nd'
  if (position === 3) return 'rd'
  return 'th'
}

const Formula1SelectPositionModal: React.FC<Formula1SelectPositionModalProps> = ({
  isOpen,
  position,
  availableDrivers,
  onClose,
  onSelect,
}) => {
  const { t } = useTranslation()
  if (!isOpen) return null

  const title = t('formula1.predictPosition', {
    position: `${position}${positionSuffix(position)}`,
  })

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="blue-sky-card rounded-t-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 w-full sm:max-w-lg sm:mx-4 shadow-2xl animate-scale-in max-h-[85vh] sm:max-h-[80vh] flex flex-col">
        <div className="sm:hidden w-12 h-1 bg-white/30 rounded-full mx-auto mb-3" />

        <div className="text-center mb-4 sm:mb-5 lg:mb-6">
          <h2 className="text-base sm:text-lg lg:text-xl font-bold text-white">{title}</h2>
          <p className="text-white/60 text-xs sm:text-sm mt-1">
            {t('formula1.selectOneDriver')}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="space-y-2">
            {availableDrivers.map((driver) => (
              <button
                key={driver.value}
                onClick={() => onSelect(driver.value)}
                className="w-full px-4 py-3 sm:py-3.5 bg-gradient-to-r from-sky-500/80 to-blue-600/80 hover:from-sky-500 hover:to-blue-600 active:scale-[0.98] text-white rounded-xl transition-all duration-200 shadow-md hover:shadow-lg font-semibold text-sm sm:text-base text-left flex items-center justify-between gap-2"
              >
                <span className="truncate">{driver.label}</span>
                <span className="text-white/80 text-xs sm:text-sm shrink-0">({driver.car})</span>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 sm:mt-5 lg:mt-6 pt-4 sm:pt-5 lg:pt-6 border-t border-white/20">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 sm:px-6 sm:py-3 lg:py-3.5 bg-white/10 hover:bg-white/20 active:bg-white/30 text-white rounded-xl transition-all duration-200 font-semibold text-sm sm:text-base"
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default Formula1SelectPositionModal
